import React from 'react'
import { useQueryClient } from 'react-query'

import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAlert } from '@hooks/useAlert'
import { Box } from '@features/profile/components/Box'
import { Button } from '@components/Buttons'
import { PenNameType } from '@models/penName/PenNameType'
import { PenNameFormType } from '@models/penName/PenNameFormType'
import { PenNameSearchSelect } from '@features/profile/components/PenNameSearchSelect'
import { usePenNameAction } from '@hooks/penName/usePenNameAction'
import { EditeIcon } from '@components/icons/EditeIcon'
import { PenNameIcon } from '@components/icons/PenNameIcon'
import { TrashIcon } from '@components/icons/TrashIcon'
import { SettingPenNameProps } from './interface'

export function SettingPenName({
  userId,
  queryKey,
  penNameList,
  handleTextSearchChange,
}: SettingPenNameProps) {
  const alert = useAlert()
  const penNameClient = usePenNameAction()
  const queryClient = useQueryClient()
  const errorPenNameModal = useModal({
    modal: 'errorPenName',
  })

  const addPenNameModal = useModal(
    {
      modal: 'addPenName',
      modalProps: {
        penNameList: penNameList?.map(row => row.firstPenName) ?? [],
        onSubmit: async (form: PenNameFormType) => {
          try {
            const newData = await penNameClient.createPenName(form)

            if (newData) {
              queryClient.setQueryData<PenNameType[]>(
                queryKey,
                (oldData = []) => [newData, ...oldData]
              )
              alert.success('เพิ่มนามปากกาสำเร็จ')
              await queryClient.refetchQueries([
                'pen-name-options-by-user',
                userId,
              ])
            }
          } catch (_) {
            alert.error('เกิดข้อผิดพลาด ไม่สามารถเพิ่มนามปากกาได้')
          } finally {
            addPenNameModal.hide()
          }
        },
      },
    },
    true
  )

  const editPenNameModal = useModal(
    {
      modal: 'editPenName',
      modalProps: {
        penNameList: [],
        onSubmit: async (form: PenNameFormType) => {
          try {
            const updateDate = await penNameClient.editPenName(form)

            if (updateDate) {
              queryClient.setQueryData<PenNameType[]>(
                queryKey,
                (oldData = []) => {
                  return oldData.map(row => {
                    if (row.id === updateDate.id) {
                      return updateDate
                    }

                    return row
                  })
                }
              )
              alert.success('แก้ไขนามปากกาสำเร็จ')
              await queryClient.refetchQueries([
                'pen-name-options-by-user',
                userId,
              ])
            }
          } catch (_) {
            alert.error('เกิดข้อผิดพลาด ไม่สามารถแก้ไขนามปากกาได้')
          } finally {
            editPenNameModal.hide()
          }
        },
      },
    },
    true
  )

  const deletePenNameModal = useModal(
    {
      modal: 'deletePenName',
    },
    true
  )

  return (
    <Box className='mobile:!px-0'>
      <div className='flex justify-between mobile:flex-col'>
        <p className='font-mitr topic-base text-left mobile:mb-[10px] dark:text-dark-10'>
          จัดการนามปากกา
        </p>
        <div className='w-[300px] mobile:w-full'>
          <PenNameSearchSelect
            userId={userId}
            onSearch={handleTextSearchChange}
          />
        </div>
      </div>
      <div className='grid grid-cols-2 gap-[20px] mt-[20px] mobile:grid-cols-1'>
        <div className='flex justify-center w-full border border-dashed border-gray dark:border-purple py-[10px] m-auto rounded-[8px]'>
          <Button type='button' onClick={() => addPenNameModal.show()}>
            <PenNameIcon
              className='text-primary pr-[10px]'
              width='40'
              height='25'
            />
            <span className='font-mitr text-[14px] font-medium text-primary underline'>
              เพิ่มนามปากกา
            </span>
          </Button>
        </div>
        {penNameList.map((item: PenNameType) => (
          <div
            key={item.id}
            className='flex justify-between items-center w-full border border-gray-3 dark:border-purple p-[15px] rounded-[8px]'
          >
            <p className='text-base font-bold text-secondary dark:text-dark-10 overflow-hidden text-ellipsis w-[220px] whitespace-nowrap'>
              {item.firstPenName}
            </p>
            <div className='flex space-x-[10px]'>
              <button
                className='border border-gray-3 dark:border-purple p-[3px] rounded-[8px]'
                onClick={() =>
                  editPenNameModal.show({
                    initialValues: {
                      id: item.id,
                      primaryPenName: item.firstPenName,
                      secondaryPenName: item.secondPenName,
                      fbUrl: item.facebookLink,
                      twUrl: item.twitterLink,
                    },
                    penNameList: penNameList
                      .filter(row => row.id !== item.id)
                      .map(row => row.firstPenName),
                  })
                }
              >
                <EditeIcon
                  width='20'
                  height='20'
                  className='text-secondary-1 dark:text-dark-10'
                />
              </button>
              <button
                className='border border-gray-3 dark:border-purple p-[3px] rounded-[8px]'
                onClick={() =>
                  deletePenNameModal.show({
                    onSubmit: async () => {
                      try {
                        const result = await penNameClient.removePenName(
                          item.id
                        )

                        if (result) {
                          queryClient.setQueryData<PenNameType[]>(
                            queryKey,
                            (oldData = []) => {
                              return oldData.filter(row => row.id !== result.id)
                            }
                          )

                          alert.success('ลบนามปากกาสำเร็จ')
                          await queryClient.refetchQueries([
                            'pen-name-options-by-user',
                            userId,
                          ])
                          deletePenNameModal.hide()
                        }
                      } catch (_) {
                        errorPenNameModal.show()
                      }
                    },
                  })
                }
              >
                <TrashIcon
                  width='20'
                  height='20'
                  className='text-secondary-1 dark:text-dark-10'
                />
              </button>
            </div>
          </div>
        ))}
      </div>
    </Box>
  )
}
